import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { Box } from '@/components/Box/Box';
import { Typography } from '@/components/Typography/Typography';
import { ButtonNew } from '@/components-new/ButtonNew/ButtonNew';
import { Notification } from '@/components/Notification/Notification';
import { getDevelopersPath } from '@/bundle/_MasterAdmin/_DevelopersPage/path/path';
import { INVOICE_VAULT_QUERY_KEYS, loadWebhooks } from '../../api/api';
import { SnippetBox } from '../../styles';
import { WebhooksPanel } from './ui/WebhooksPanel';
import { useUser } from '@/context/userContext';
import { DetailsWidget } from '@/components/DetailsWidget/DetailsWidget';
import { PageContent } from '@/components/Page/PageContent/PageContent';
import { PageColumn } from '@/components/Page/PageColumn/PageColumn';

const REQUEST_SNIPPET = `POST /papi/v1/invoices/secure-link
x-api-id: <key id>
x-api-key: <key>

{
  "invoice_number": "INV-1050",
  "amount": 1850.00,
  "currency": "USD",
  "due_date": "2026-11-15",
  "policy": "verify",
  "expires_in_days": 30,
  "external_ref": "qb-7781"
}`;

const RESPONSE_SNIPPET = `201 Created

{
  "token": "INV-1050-K7PQ",
  "status": "active",
  "paste_kit": {
    "secure_verification_url": "https://wirevault.io/i/INV-1050-K7PQ",
    "qr_code_svg": "<svg ...>",
    "action_snippet_html": "<a class=\\"wirevault-link\\" href=\\"...\\">Verify bank details before paying</a>",
    "expires_at": "2026-12-15T00:00:00Z"
  }
}`;

export const OpenApiTab = () => {
  const navigate = useNavigate();
  const { isMasterAdminRole, user } = useUser();
  const { data, refetch } = useQuery({ queryKey: INVOICE_VAULT_QUERY_KEYS.webhooks, queryFn: loadWebhooks });

  const hasAccessToAPIKeys = !!user?.organization_user?.organization?.is_api_keys_allowed;

  return (
    <PageContent>
      <PageColumn>
        <Box display='flex' flexDirection='column' gap='24px'>
          <DetailsWidget>
            <Box display='flex' flexDirection='column' gap='20px'>
              <Typography variant='subTitle'>Create a secure link per invoice</Typography>
              <Typography variant='bodySmall'>
                One call per invoice. The response carries the link, the QR as SVG and the footer snippet. `policy` is
                `share` or `verify`; `account_id` and `rail` are optional when a default account is set on the
                Integrations tab.
              </Typography>
              <SnippetBox>{REQUEST_SNIPPET}</SnippetBox>
              <SnippetBox>{RESPONSE_SNIPPET}</SnippetBox>
              <Typography variant='caption'>
                Also: <code>GET /papi/v1/invoices/secure-link/&lt;token&gt;</code>,{' '}
                <code>POST .../&lt;token&gt;/revoke</code>, <code>GET /papi/v1/vault/accounts</code> (names and payment
                types only, never numbers).
              </Typography>
            </Box>
          </DetailsWidget>

          <DetailsWidget>
            <Box display='flex' flexDirection='column' gap='20px' alignItems='flex-start'>
              <Typography variant='subTitle'>API keys</Typography>
              <Typography variant='bodySmall'>
                Keys are created and rotated on the Developers page. Each call is logged with the key that made it.
              </Typography>
              <Notification variant='warning'>Manage API keys feature disabled for this organization.</Notification>
              {isMasterAdminRole ? (
                <ButtonNew
                  size='medium'
                  variant='secondary'
                  disabled={!hasAccessToAPIKeys}
                  onClick={() => navigate(getDevelopersPath())}
                >
                  Manage API keys
                </ButtonNew>
              ) : (
                <Notification variant='info'>Only master admins manage API keys.</Notification>
              )}
              <Typography variant='caption'>
                Connected apps (OAuth 2.0) are a follow-up; see the handoff open items.
              </Typography>
            </Box>
          </DetailsWidget>
        </Box>
      </PageColumn>
      <PageColumn>
        <WebhooksPanel
          endpoints={data?.body?.results ?? []}
          events={data?.body?.events ?? []}
          canEdit={isMasterAdminRole}
          onChanged={refetch}
        />
      </PageColumn>
    </PageContent>
  );
};
